import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Clock, Wheat, Flame } from "lucide-react";
import { LineReveal, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { HoursTable } from "../components/HoursTable";
import {
  HERO_IMAGE,
  INTERIOR_IMAGE,
  PRODUCTS,
  CHAPTERS,
  BUSINESS,
} from "../lib/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative bg-cream pt-24 pb-0 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch pt-4 lg:pt-6">
        <div className="lg:col-span-6 flex flex-col justify-between py-1">
          <div>
            <Reveal delay={0.05}>
              <p className="kicker text-crust mb-5">
                Boulangerie artisanale · Nantes depuis 2011
              </p>
            </Reveal>
            <LineReveal
              as="h1"
              className="font-serif font-light text-charcoal text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
              lines={["Le pain,", "à la main,", "chaque matin."]}
              delay={0.2}
            />
            <Reveal delay={0.75}>
              <p className="mt-7 max-w-md text-base sm:text-lg text-ash leading-relaxed">
                Levain naturel, farines de meule et cuisson à la pierre. Une
                boulangerie de quartier où l'on prend le temps de bien faire.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.9}>
            <div className="mt-8 lg:mt-4 flex flex-wrap gap-3">
              <Link
                to="/produits"
                data-testid="hero-cta-products"
                className="kicker bg-crust text-cream px-7 py-4 transition-transform duration-200 active:scale-95 hover:bg-crustdark"
              >
                Découvrir nos pains
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="kicker text-charcoal border border-charcoal/30 px-7 py-4 transition-colors duration-300 hover:border-crust hover:text-crust"
              >
                Nous trouver
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden h-[46vh] sm:h-[52vh] lg:h-full w-full"
          >
            <motion.img
              src={HERO_IMAGE}
              alt="Miche de pain fraîchement cuite tenue à deux mains"
              style={{ y, scale }}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </div>

      {/* Bandeau d'infos rapides */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full mt-8 lg:mt-10">
        <Reveal delay={0.2}>
          <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-crustborder">
            {[
              { icon: Wheat, label: "Levain naturel", sub: "Entretenu chaque jour" },
              { icon: Flame, label: "Cuisson à la pierre", sub: "Farines de meule" },
              { icon: Clock, label: "Ouvert 6 j/7", sub: "Dès 6h30 le matin" },
            ].map((f, i) => (
              <div
                key={f.label}
                className={`flex items-center gap-4 py-6 sm:py-7 ${
                  i < 2 ? "sm:border-r border-crustborder" : ""
                } ${i > 0 ? "border-t sm:border-t-0 border-crustborder" : ""} sm:px-6`}
              >
                <f.icon size={26} className="text-crust shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-xl text-charcoal leading-none">
                    {f.label}
                  </p>
                  <p className="text-sm text-ash mt-1">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Chapter = ({ chapter, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`chapter-${chapter.n}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center"
    >
      <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <div className="overflow-hidden aspect-[4/3] lg:aspect-[4/5] w-full">
          <motion.img
            src={chapter.image}
            alt={chapter.title}
            style={{ y }}
            className="w-full h-[112%] object-cover"
          />
        </div>
      </div>
      <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <Reveal>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-5xl sm:text-6xl text-crust/30 leading-none">
              {chapter.n}
            </span>
            <h3 className="font-serif text-3xl sm:text-4xl text-charcoal">
              {chapter.title}
            </h3>
          </div>
          <p className="mt-4 text-base text-ash leading-relaxed max-w-lg">
            {chapter.text}
          </p>
        </Reveal>
      </div>
    </div>
  );
};

const ProductPreview = () => (
  <section
    data-testid="product-preview"
    className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20"
  >
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
      <div>
        <Reveal>
          <p className="kicker text-crust mb-3">La vitrine</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-charcoal leading-none">
            Ce qui sort du four
          </h2>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <Link
          to="/produits"
          data-testid="preview-all-products"
          className="kicker text-charcoal border-b border-crust pb-1 hover:text-crust transition-colors duration-300 w-fit"
        >
          Voir tous les produits →
        </Link>
      </Reveal>
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {PRODUCTS.slice(0, 3).map((p, i) => (
        <Reveal key={p.name} delay={i * 0.06} className={i === 2 ? "col-span-2 lg:col-span-1" : ""}>
          <Link to="/produits" className="group block">
            <div className="overflow-hidden aspect-[4/5] w-full mb-4">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
            <p className="kicker text-crust mb-1.5">{p.category}</p>
            <h3 className="font-serif text-2xl sm:text-3xl text-charcoal">{p.name}</h3>
            <p className="mt-2 text-sm text-ash leading-relaxed">{p.desc}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />

      <Marquee
        items={[
          "Levain naturel",
          "Farine de meule",
          "Cuisson à la pierre",
          "Nantes",
          "Fait main",
        ]}
      />

      {/* Manifeste / savoir-faire */}
      <section
        data-testid="manifesto-section"
        className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="kicker text-crust mb-3">Le savoir-faire</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-charcoal leading-[1.05] max-w-xl">
                Trois gestes, rien de plus, rien de moins.
              </h2>
              <p className="mt-5 text-ash leading-relaxed max-w-md">
                Chaque pain naît d'un travail patient : de la farine au four,
                nous ne prenons aucun raccourci.
              </p>
            </Reveal>
          </div>

          {/* Encadré horaires */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div
                data-testid="home-hours-card"
                className="border border-crustborder bg-dough p-7 sm:p-8"
              >
                <div className="flex items-center gap-3 mb-5">
                  <Clock size={20} className="text-crust" strokeWidth={1.5} />
                  <p className="kicker text-crust">Horaires d'ouverture</p>
                </div>
                <HoursTable tone="light" testId="home-hours" />
              </div>
            </Reveal>
          </div>
        </div>
        <div className="flex flex-col gap-14 md:gap-16">
          {CHAPTERS.map((c, i) => (
            <Chapter key={c.n} chapter={c} index={i} />
          ))}
        </div>
      </section>

      {/* Bande image + citation */}
      <section className="grain relative h-[42vh] sm:h-[46vh] overflow-hidden">
        <img
          src={INTERIOR_IMAGE}
          alt="Intérieur chaleureux de la boulangerie"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/45" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            <Reveal>
              <p className="font-serif italic text-cream text-3xl sm:text-5xl max-w-2xl leading-tight">
                « Un bon pain se reconnaît à sa croûte qui chante en
                refroidissant. »
              </p>
              <p className="kicker text-cream/70 mt-5">Pierre, boulanger</p>
            </Reveal>
          </div>
        </div>
      </section>

      <ProductPreview />

      {/* Bandeau visite / contact compact */}
      <section className="grain bg-dough border-t border-crustborder">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl text-charcoal leading-tight">
                Passez pousser la porte.
              </h2>
              <p className="mt-3 text-ash max-w-md">
                {BUSINESS.addressLine} — le café est prêt et les fournées aussi.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-3">
              <a
                href={BUSINESS.phoneLink}
                data-testid="home-phone-cta"
                className="kicker bg-crust text-cream px-7 py-4 transition-transform duration-200 active:scale-95 hover:bg-crustdark"
              >
                {BUSINESS.phoneDisplay}
              </a>
              <Link
                to="/contact"
                data-testid="home-contact-cta"
                className="kicker text-charcoal border border-charcoal/30 px-7 py-4 transition-colors duration-300 hover:border-crust hover:text-crust"
              >
                Nous écrire
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
